/**
 * client/ui/season-prizes.ts
 * Responsabilidad: Vista y controlador modular de Premios y Títulos de Temporada.
 *
 * Características:
 * - Píldoras holográficas animadas con gradientes, resplandores y tipografía de prestigio.
 * - Desglose dual: Estadísticas de la Temporada Activa vs Histórico Vitalicio.
 * - Banner dorado pulsante para reclamar premios acumulados de fin de mes.
 * - Armería de Títulos Honoríficos: equipar/desequipar títulos y guía de desbloqueo de títulos especiales.
 */

import Swal from "sweetalert2";
import { safeGetStorage } from "../utils";
import { syncTitlesCatalogFromServer } from "../hologram-titles";

interface TitleMetadata {
    id: string;
    titleId?: string;
    name: string;
    category: string;
    rankTier: number;
    hologramStyle: string;
    primaryColor: string;
    secondaryColor: string;
    icon: string;
    durationDays: number;
    description: string;
    expiresAt?: string;
}

interface SeasonRewardItem {
    id: number;
    leaderboardType: string;
    rankFrom: number;
    rankTo: number;
    crystalsReward: number;
    orbsReward: number;
    titleId: string;
    titleName: string;
    hologramStyle: string;
    primaryColor: string;
    secondaryColor: string;
    icon: string;
    durationDays: number;
    description: string;
}

interface SeasonOverviewData {
    seasonKey: string;
    seasonEndUtc: string;
    msUntilSeasonEnd: number;
    maxRankRewarded: number;
    rewardsCatalog: SeasonRewardItem[];
    specialTitles: TitleMetadata[];
    titlesCatalog?: TitleMetadata[];
    leaderboards: {
        kills: Array<{ userId: string; alias: string; country?: string; seasonKills: number }>;
        mass: Array<{ userId: string; alias: string; country?: string; seasonBestMass: number }>;
        streak: Array<{ userId: string; alias: string; country?: string; seasonBestStreak: number }>;
        time: Array<{ userId: string; alias: string; country?: string; seasonTimeMs: number }>;
    };
    playerSeasonStats?: {
        seasonKills: number;
        seasonBestMass: number;
        seasonBestStreak: number;
        seasonTimeMs: number;
        seasonGames: number;
        ranks: { kills: number; mass: number; streak: number; time: number };
    };
    playerAllTimeStats?: {
        totalKills: number;
        bestMass: number;
        bestStreak: number;
        totalTimeMs: number;
        totalGames: number;
    };
    unlockedTitles?: TitleMetadata[];
    equippedTitle?: string;
    pendingSeasonRewards?: Array<{
        seasonKey: string;
        leaderboardType: string;
        rank: number;
        crystals: number;
        orbs: number;
        titleId: string;
        titleName: string;
    }>;
}

export class SeasonPrizesUI {
    private static activeCategory: "kills" | "mass" | "streak" | "time" = "kills";
    private static statsDisplayMode: "season" | "alltime" = "season";
    private static armoryFilter: string = "all";
    private static cachedOverview: SeasonOverviewData | null = null;
    private static lastFetchTime: number = 0;
    private static onBalancesUpdatedCallback?: (crystals: number, orbs: number) => void;

    /**
     * Formatea insignias holográficas de forma simétrica sin dañar las letras en español.
     */
    public static formatTitleBadge(icon: string, rawName: string): string {
        const cleanName = (rawName || "").replace(/^[\p{Extended_Pictographic}\uFE0F\s]+/u, "").trim();
        const ic = (icon || "").trim();
        return ic ? `⟪ ${ic} ${cleanName} ${ic} ⟫` : `⟪ ${cleanName} ⟫`;
    }

    /**
     * Inyecta la hoja de estilos CSS dedicada con animaciones y efectos holográficos.
     */
    private static injectStyles(): void {
        if (document.getElementById("season-prizes-styles")) return;

        const style = document.createElement("style");
        style.id = "season-prizes-styles";
        style.textContent = `
            .season-prizes-view {
                display: flex;
                flex-direction: column;
                gap: 20px;
                padding: 10px 4px;
                color: #f8fafc;
                font-family: inherit;
            }

            /* Banner Dorado Pulsante para Premios Listos */
            .season-claim-banner {
                position: relative;
                overflow: hidden;
                background: linear-gradient(135deg, rgba(245, 158, 11, 0.3) 0%, rgba(217, 119, 6, 0.45) 50%, rgba(251, 191, 36, 0.25) 100%);
                border: 1.5px solid #fbbf24;
                border-radius: 14px;
                padding: 16px 20px;
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: space-between;
                gap: 14px;
                box-shadow: 0 0 25px rgba(251, 191, 36, 0.4);
                animation: banner-pulse 3s infinite ease-in-out;
            }

            @keyframes banner-pulse {
                0%, 100% { box-shadow: 0 0 20px rgba(251, 191, 36, 0.35); border-color: #fbbf24; }
                50% { box-shadow: 0 0 35px rgba(251, 191, 36, 0.65); border-color: #fef08a; }
            }

            .season-claim-info {
                display: flex;
                align-items: center;
                gap: 14px;
            }

            .season-claim-icon {
                font-size: 2.2rem;
                animation: float-gem 2s infinite ease-in-out;
            }

            @keyframes float-gem {
                0%, 100% { transform: translateY(0) scale(1); }
                50% { transform: translateY(-4px) scale(1.08); }
            }

            .season-claim-title {
                margin: 0;
                font-size: 1.05rem;
                font-weight: 800;
                color: #fef08a;
                text-shadow: 0 0 10px rgba(251, 191, 36, 0.6);
                letter-spacing: 0.5px;
            }

            .season-claim-desc {
                margin: 3px 0 0 0;
                font-size: 0.85rem;
                color: #fef3c7;
                opacity: 0.9;
            }

            .season-claim-btn {
                background: linear-gradient(135deg, #f59e0b, #d97706);
                border: 1px solid #fef08a;
                color: #0f172a;
                font-weight: 800;
                font-size: 0.95rem;
                padding: 10px 22px;
                border-radius: 10px;
                cursor: pointer;
                box-shadow: 0 4px 14px rgba(245, 158, 11, 0.5);
                transition: all 0.2s ease;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .season-claim-btn:hover {
                transform: translateY(-2px) scale(1.03);
                background: linear-gradient(135deg, #fbbf24, #f59e0b);
                box-shadow: 0 6px 20px rgba(251, 191, 36, 0.7);
            }

            /* Resumen de Estadísticas del Jugador */
            .season-stats-container {
                background: rgba(15, 23, 42, 0.65);
                border: 1px solid rgba(255, 255, 255, 0.08);
                border-radius: 14px;
                padding: 16px;
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .season-stats-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex-wrap: wrap;
                gap: 10px;
            }

            .season-stats-title {
                margin: 0;
                font-size: 0.95rem;
                font-weight: 700;
                color: #94a3b8;
                text-transform: uppercase;
                letter-spacing: 0.8px;
            }

            .season-mode-toggle {
                display: flex;
                background: rgba(0, 0, 0, 0.35);
                border-radius: 8px;
                padding: 3px;
                border: 1px solid rgba(255, 255, 255, 0.06);
            }

            .season-toggle-btn {
                background: transparent;
                border: none;
                color: #94a3b8;
                font-size: 0.8rem;
                font-weight: 600;
                padding: 5px 12px;
                border-radius: 6px;
                cursor: pointer;
                transition: all 0.2s ease;
            }

            .season-toggle-btn.active {
                background: rgba(245, 158, 11, 0.25);
                color: #fde047;
                box-shadow: 0 0 10px rgba(245, 158, 11, 0.3);
            }

            .season-cards-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                gap: 10px;
            }

            .season-stat-card {
                background: rgba(30, 41, 59, 0.6);
                border: 1px solid rgba(255, 255, 255, 0.06);
                border-radius: 10px;
                padding: 12px 14px;
                display: flex;
                flex-direction: column;
                gap: 6px;
                transition: transform 0.2s ease, border-color 0.2s ease;
            }

            .season-stat-card:hover {
                transform: translateY(-2px);
                border-color: rgba(245, 158, 11, 0.4);
            }

            .season-stat-top {
                display: flex;
                align-items: center;
                justify-content: space-between;
                font-size: 0.8rem;
                color: #94a3b8;
            }

            .season-rank-badge {
                font-size: 0.8rem;
                font-weight: 800;
                padding: 2px 7px;
                border-radius: 6px;
                background: rgba(245, 158, 11, 0.15);
                color: #fbbf24;
                border: 1px solid rgba(245, 158, 11, 0.3);
            }

            .season-stat-value {
                font-size: 1.35rem;
                font-weight: 800;
                color: #f8fafc;
            }

            .season-stat-sub {
                font-size: 0.75rem;
                color: #64748b;
            }

            /* Categorías de Premios */
            .season-cat-tabs {
                display: flex;
                gap: 8px;
                overflow-x: auto;
                padding-bottom: 4px;
            }

            .season-cat-btn {
                background: rgba(30, 41, 59, 0.6);
                border: 1px solid rgba(255, 255, 255, 0.08);
                color: #94a3b8;
                padding: 8px 16px;
                border-radius: 9px;
                font-size: 0.85rem;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.2s ease;
                white-space: nowrap;
            }

            .season-cat-btn:hover {
                color: #f8fafc;
                border-color: rgba(255, 255, 255, 0.2);
            }

            .season-cat-btn.active {
                background: rgba(245, 158, 11, 0.2);
                border-color: #f59e0b;
                color: #fde047;
                box-shadow: 0 0 12px rgba(245, 158, 11, 0.25);
            }

            /* Tabla de Premios */
            .season-rewards-table-wrap {
                background: rgba(15, 23, 42, 0.65);
                border: 1px solid rgba(255, 255, 255, 0.08);
                border-radius: 14px;
                overflow-x: auto;
                -webkit-overflow-scrolling: touch;
                width: 100%;
            }

            @media (max-width: 640px) {
                .season-prizes-view {
                    gap: 12px !important;
                    padding: 2px 0 !important;
                }
                .season-cards-grid {
                    grid-template-columns: repeat(2, 1fr) !important;
                    gap: 8px !important;
                }
                .season-stat-card {
                    padding: 8px 10px !important;
                }
                .season-stat-value {
                    font-size: 1.15rem !important;
                }
                .season-armory-grid {
                    grid-template-columns: 1fr !important;
                }
                .season-rewards-table th,
                .season-rewards-table td {
                    padding: 8px 10px !important;
                    font-size: 0.78rem !important;
                }
                .season-cat-tabs {
                    gap: 6px !important;
                }
                .season-cat-btn {
                    padding: 6px 10px !important;
                    font-size: 0.78rem !important;
                }
                .hologram-pill {
                    font-size: 0.74rem !important;
                    padding: 3px 8px !important;
                }
            }

            .season-rewards-table {
                width: 100%;
                border-collapse: collapse;
                text-align: left;
                font-size: 0.85rem;
            }

            .season-rewards-table th {
                background: rgba(30, 41, 59, 0.7);
                padding: 12px 14px;
                color: #94a3b8;
                font-weight: 700;
                text-transform: uppercase;
                font-size: 0.75rem;
                letter-spacing: 0.5px;
                border-bottom: 1px solid rgba(255, 255, 255, 0.08);
            }

            .season-rewards-table td {
                padding: 12px 14px;
                border-bottom: 1px solid rgba(255, 255, 255, 0.04);
                vertical-align: middle;
            }

            .season-rewards-table tr:last-child td {
                border-bottom: none;
            }

            .season-rewards-table tr:hover {
                background: rgba(255, 255, 255, 0.02);
            }

            .rank-cell-top1 { color: #facc15; font-weight: 800; font-size: 1rem; }
            .rank-cell-top2 { color: #cbd5e1; font-weight: 700; }
            .rank-cell-top3 { color: #d97706; font-weight: 700; }

            /* Píldoras Holográficas */
            .hologram-pill {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                padding: 5px 12px;
                border-radius: 20px;
                font-size: 0.82rem;
                font-weight: 800;
                letter-spacing: 0.4px;
                text-transform: uppercase;
                transition: all 0.3s ease;
                position: relative;
                overflow: hidden;
                box-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
            }

            .hologram-pill::before {
                content: "";
                position: absolute;
                top: 0; left: -100%; width: 100%; height: 100%;
                background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent);
                animation: holo-shine 4s infinite linear;
            }

            @keyframes holo-shine {
                0% { left: -100%; }
                20%, 100% { left: 200%; }
            }

            .hologram-cyber_gold {
                background: linear-gradient(135deg, rgba(251, 191, 36, 0.25), rgba(245, 158, 11, 0.1));
                border: 1px solid #fbbf24;
                color: #fef08a;
                box-shadow: 0 0 14px rgba(251, 191, 36, 0.45);
            }

            .hologram-crimson_plasma {
                background: linear-gradient(135deg, rgba(239, 68, 68, 0.25), rgba(220, 38, 38, 0.1));
                border: 1px solid #ef4444;
                color: #fca5a5;
                box-shadow: 0 0 14px rgba(239, 68, 68, 0.45);
            }

            .hologram-astral_nebula {
                background: linear-gradient(135deg, rgba(168, 85, 247, 0.25), rgba(56, 189, 248, 0.15));
                border: 1px solid #a855f7;
                color: #e9d5ff;
                box-shadow: 0 0 14px rgba(168, 85, 247, 0.45);
            }

            .hologram-void_glitch {
                background: linear-gradient(135deg, rgba(236, 72, 153, 0.25), rgba(244, 63, 94, 0.15));
                border: 1px solid #ec4899;
                color: #fce7f3;
                box-shadow: 0 0 14px rgba(236, 72, 153, 0.45);
            }

            .hologram-emerald_matrix {
                background: linear-gradient(135deg, rgba(16, 185, 129, 0.25), rgba(5, 150, 105, 0.1));
                border: 1px solid #10b981;
                color: #a7f3d0;
                box-shadow: 0 0 14px rgba(16, 185, 129, 0.45);
            }

            /* Armería de Títulos del Jugador */
            .season-armory-section {
                background: rgba(15, 23, 42, 0.65);
                border: 1px solid rgba(255, 255, 255, 0.08);
                border-radius: 14px;
                padding: 16px;
                display: flex;
                flex-direction: column;
                gap: 14px;
            }

            .season-armory-title {
                margin: 0;
                font-size: 1rem;
                font-weight: 700;
                color: #f8fafc;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .season-armory-tabs {
                display: flex;
                gap: 6px;
                overflow-x: auto;
                padding-bottom: 4px;
                margin-bottom: 10px;
            }

            .armory-filter-btn {
                background: rgba(30, 41, 59, 0.6);
                border: 1px solid rgba(255, 255, 255, 0.08);
                color: #94a3b8;
                padding: 5px 12px;
                border-radius: 8px;
                font-size: 0.8rem;
                font-weight: 600;
                cursor: pointer;
                transition: all 0.2s ease;
                white-space: nowrap;
            }

            .armory-filter-btn:hover {
                color: #f8fafc;
                border-color: rgba(255, 255, 255, 0.2);
            }

            .armory-filter-btn.active {
                background: rgba(56, 189, 248, 0.2);
                border-color: #38bdf8;
                color: #e0f2fe;
                box-shadow: 0 0 10px rgba(56, 189, 248, 0.25);
            }

            .season-armory-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                gap: 10px;
                max-height: 280px;
                overflow-y: auto;
                padding-right: 4px;
            }

            .season-armory-grid::-webkit-scrollbar {
                width: 5px;
            }

            .season-armory-grid::-webkit-scrollbar-thumb {
                background: rgba(56, 189, 248, 0.3);
                border-radius: 4px;
            }

            .season-equipped-banner {
                background: linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(30, 41, 59, 0.65) 100%);
                border: 1px solid rgba(251, 191, 36, 0.4);
                border-radius: 12px;
                padding: 10px 14px;
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: space-between;
                gap: 10px;
                margin-bottom: 12px;
                box-shadow: 0 0 15px rgba(245, 158, 11, 0.15);
            }

            .season-equipped-left {
                display: flex;
                align-items: center;
                gap: 10px;
                flex-wrap: wrap;
            }

            .season-equipped-label {
                font-size: 0.72rem;
                font-weight: 800;
                color: #fbbf24;
                letter-spacing: 0.5px;
                text-transform: uppercase;
            }

            .season-title-card {
                background: rgba(30, 41, 59, 0.5);
                border: 1px solid rgba(255, 255, 255, 0.08);
                border-radius: 10px;
                padding: 12px 14px;
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 10px;
                transition: all 0.2s ease;
            }

            .season-title-card.equipped {
                border-color: #fbbf24;
                background: rgba(245, 158, 11, 0.12);
                box-shadow: 0 0 12px rgba(245, 158, 11, 0.25);
            }

            .season-title-info {
                display: flex;
                flex-direction: column;
                gap: 4px;
            }

            .season-title-meta {
                font-size: 0.72rem;
                color: #94a3b8;
            }

            .season-equip-btn {
                background: rgba(255, 255, 255, 0.08);
                border: 1px solid rgba(255, 255, 255, 0.15);
                color: #f8fafc;
                font-size: 0.75rem;
                font-weight: 700;
                padding: 6px 12px;
                border-radius: 6px;
                cursor: pointer;
                transition: all 0.2s ease;
                white-space: nowrap;
            }

            .season-equip-btn:hover {
                background: rgba(255, 255, 255, 0.16);
                border-color: rgba(255, 255, 255, 0.3);
            }

            .season-equip-btn.is-equipped {
                background: #10b981;
                border-color: #34d399;
                color: #064e3b;
            }

            .season-equip-btn.is-equipped:hover {
                background: #ef4444;
                border-color: #f87171;
                color: #fff;
            }

            /* Catálogo de Títulos Especiales Adictivos */
            .special-titles-card {
                background: rgba(30, 41, 59, 0.4);
                border: 1px dashed rgba(255, 255, 255, 0.12);
                border-radius: 10px;
                padding: 12px 14px;
                display: flex;
                flex-direction: column;
                gap: 6px;
            }

            .special-title-top {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 10px;
            }

            .special-title-req {
                font-size: 0.78rem;
                color: #cbd5e1;
                line-height: 1.35;
            }

            .special-duration-badge {
                font-size: 0.7rem;
                padding: 2px 6px;
                background: rgba(255, 255, 255, 0.08);
                border-radius: 4px;
                color: #94a3b8;
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Consulta el overview de temporada desde el backend.
     */
    public static async fetchSeasonOverview(): Promise<SeasonOverviewData | null> {
        const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
        const headers: Record<string, string> = {};
        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        }

        try {
            const res = await fetch("/api/season/overview", { headers });
            if (!res.ok) {
                throw new Error(`HTTP error ${res.status}`);
            }
            const data = await res.json();
            this.cachedOverview = data;
            this.lastFetchTime = Date.now();
            if (Array.isArray(data.titlesCatalog)) {
                void syncTitlesCatalogFromServer(data.titlesCatalog);
            }
            return data;
        } catch (err) {
            console.warn("[SeasonPrizesUI] Error consultando /api/season/overview:", err);
            return this.cachedOverview;
        }
    }

    /**
     * Renderiza la vista completa dentro del contenedor del modal.
     */
    public static async render(
        container: HTMLElement,
        onBalancesUpdated?: (crystals: number, orbs: number) => void
    ): Promise<void> {
        if (!container) return;
        this.injectStyles();
        if (onBalancesUpdated) {
            this.onBalancesUpdatedCallback = onBalancesUpdated;
        }

        container.innerHTML = `
            <div style="display:flex; justify-content:center; align-items:center; min-height:220px; color:#94a3b8;">
                <span>⏳ Cargando temporada y premios...</span>
            </div>
        `;

        const data = await this.fetchSeasonOverview();
        if (!data) {
            container.innerHTML = `
                <div style="padding:30px; text-align:center; color:#f87171;">
                    ❌ No se pudo conectar con el servicio de temporadas. Inténtalo más tarde.
                </div>
            `;
            return;
        }

        container.innerHTML = "";
        const mainWrap = document.createElement("div");
        mainWrap.className = "season-prizes-view";

        // 1. Banner de reclamo de premios acumulados si hay pendientes
        const pendingRewards = data.pendingSeasonRewards || [];
        if (pendingRewards.length > 0) {
            const totalCrystals = pendingRewards.reduce((acc, r) => acc + (r.crystals || 0), 0);
            const totalOrbs = pendingRewards.reduce((acc, r) => acc + (r.orbs || 0), 0);

            const banner = document.createElement("div");
            banner.className = "season-claim-banner";
            banner.innerHTML = `
                <div class="season-claim-info">
                    <div class="season-claim-icon">🎁</div>
                    <div>
                        <h4 class="season-claim-title">¡TIENES PREMIOS DE FIN DE TEMPORADA LISTOS!</h4>
                        <p class="season-claim-desc">Reclama +${totalCrystals.toLocaleString()} 💎 Cristales, +${totalOrbs.toLocaleString()} 🔮 Orbes y tus Títulos Honoríficos.</p>
                    </div>
                </div>
                <button type="button" class="season-claim-btn" id="btn-claim-season-rewards">
                    <span>👑 RECLAMAR RECOMPENSAS</span>
                </button>
            `;

            const claimBtn = banner.querySelector("#btn-claim-season-rewards");
            if (claimBtn) {
                claimBtn.addEventListener("click", async () => {
                    await this.handleClaimRewards(claimBtn as HTMLButtonElement, container);
                });
            }
            mainWrap.appendChild(banner);
        }

        // 2. Tarjetas de Estadísticas del Jugador (Dual: Temporada vs Vitalicio)
        const statsSection = document.createElement("div");
        statsSection.className = "season-stats-container";

        const pStats = data.playerSeasonStats || { seasonKills: 0, seasonBestMass: 0, seasonBestStreak: 0, seasonTimeMs: 0, seasonGames: 0, ranks: { kills: 0, mass: 0, streak: 0, time: 0 } };
        const allStats = data.playerAllTimeStats || { totalKills: 0, bestMass: 0, bestStreak: 0, totalTimeMs: 0, totalGames: 0 };

        statsSection.innerHTML = `
            <div class="season-stats-header">
                <span class="season-stats-title">📊 Mi Progreso Competitivo</span>
                <div class="season-mode-toggle">
                    <button type="button" class="season-toggle-btn ${this.statsDisplayMode === "season" ? "active" : ""}" id="toggle-stat-season">🔥 Temporada</button>
                    <button type="button" class="season-toggle-btn ${this.statsDisplayMode === "alltime" ? "active" : ""}" id="toggle-stat-alltime">🏛️ Histórico</button>
                </div>
            </div>
            <div class="season-cards-grid" id="season-stat-cards-grid">
                <!-- Se rellena con updateStatCards -->
            </div>
        `;

        mainWrap.appendChild(statsSection);

        // Conectar conmutador de estadísticas
        const toggleSeason = statsSection.querySelector("#toggle-stat-season");
        const toggleAllTime = statsSection.querySelector("#toggle-stat-alltime");
        const statCardsGrid = statsSection.querySelector("#season-stat-cards-grid") as HTMLElement;

        const updateStatCards = () => {
            if (!statCardsGrid) return;
            const isSeason = this.statsDisplayMode === "season";

            const killsVal = isSeason ? pStats.seasonKills : allStats.totalKills;
            const killsRank = isSeason && pStats.ranks.kills > 0 ? `#${pStats.ranks.kills}` : "—";

            const massVal = isSeason ? pStats.seasonBestMass : allStats.bestMass;
            const massRank = isSeason && pStats.ranks.mass > 0 ? `#${pStats.ranks.mass}` : "—";

            const streakVal = isSeason ? pStats.seasonBestStreak : allStats.bestStreak;
            const streakRank = isSeason && pStats.ranks.streak > 0 ? `#${pStats.ranks.streak}` : "—";

            const timeVal = isSeason ? pStats.seasonTimeMs : allStats.totalTimeMs;
            const timeRank = isSeason && pStats.ranks.time > 0 ? `#${pStats.ranks.time}` : "—";

            statCardsGrid.innerHTML = `
                <div class="season-stat-card">
                    <div class="season-stat-top">
                        <span>⚔️ Eliminaciones</span>
                        ${isSeason ? `<span class="season-rank-badge">${killsRank}</span>` : ""}
                    </div>
                    <div class="season-stat-value">${killsVal.toLocaleString()}</div>
                    <div class="season-stat-sub">${isSeason ? "Bajas este mes" : "Bajas históricas"}</div>
                </div>
                <div class="season-stat-card">
                    <div class="season-stat-top">
                        <span>🪐 Masa Máxima</span>
                        ${isSeason ? `<span class="season-rank-badge">${massRank}</span>` : ""}
                    </div>
                    <div class="season-stat-value">${massVal.toLocaleString()}</div>
                    <div class="season-stat-sub">${isSeason ? "Pico de este mes" : "Pico histórico"}</div>
                </div>
                <div class="season-stat-card">
                    <div class="season-stat-top">
                        <span>🔥 Mejor Racha</span>
                        ${isSeason ? `<span class="season-rank-badge">${streakRank}</span>` : ""}
                    </div>
                    <div class="season-stat-value">${streakVal.toLocaleString()}</div>
                    <div class="season-stat-sub">${isSeason ? "Racha este mes" : "Racha histórica"}</div>
                </div>
                <div class="season-stat-card">
                    <div class="season-stat-top">
                        <span>⏳ Supervivencia</span>
                        ${isSeason ? `<span class="season-rank-badge">${timeRank}</span>` : ""}
                    </div>
                    <div class="season-stat-value">${this.formatDuration(timeVal)}</div>
                    <div class="season-stat-sub">${isSeason ? "Tiempo este mes" : "Tiempo total"}</div>
                </div>
            `;
        };

        if (toggleSeason && toggleAllTime) {
            toggleSeason.addEventListener("click", () => {
                this.statsDisplayMode = "season";
                toggleSeason.classList.add("active");
                toggleAllTime.classList.remove("active");
                updateStatCards();
            });
            toggleAllTime.addEventListener("click", () => {
                this.statsDisplayMode = "alltime";
                toggleAllTime.classList.add("active");
                toggleSeason.classList.remove("active");
                updateStatCards();
            });
        }
        updateStatCards();

        // 3. Tabla de Premios por Puesto Top 1..5 con Pestañas
        const prizesSection = document.createElement("div");
        prizesSection.style.display = "flex";
        prizesSection.style.flexDirection = "column";
        prizesSection.style.gap = "12px";

        const catTabs = document.createElement("div");
        catTabs.className = "season-cat-tabs";
        catTabs.innerHTML = `
            <button type="button" class="season-cat-btn ${this.activeCategory === "kills" ? "active" : ""}" data-cat="kills">⚔️ Bajas</button>
            <button type="button" class="season-cat-btn ${this.activeCategory === "mass" ? "active" : ""}" data-cat="mass">🪐 Masa</button>
            <button type="button" class="season-cat-btn ${this.activeCategory === "streak" ? "active" : ""}" data-cat="streak">🔥 Racha</button>
            <button type="button" class="season-cat-btn ${this.activeCategory === "time" ? "active" : ""}" data-cat="time">⏳ Supervivencia</button>
        `;

        const tableWrap = document.createElement("div");
        tableWrap.className = "season-rewards-table-wrap";
        tableWrap.id = "season-rewards-table-container";

        const renderRewardsTable = () => {
            const cat = this.activeCategory;
            const currentCatRewards = (data.rewardsCatalog || []).filter((r) => r.leaderboardType === cat);
            const liveLeaders = data.leaderboards?.[cat] || [];

            let rowsHtml = "";
            for (let i = 1; i <= 5; i++) {
                const r = currentCatRewards.find((item) => item.rankFrom <= i && item.rankTo >= i);
                const leader = liveLeaders[i - 1];

                let rankBadge = `#${i}`;
                let rankClass = "";
                if (i === 1) { rankBadge = "🥇 #1"; rankClass = "rank-cell-top1"; }
                else if (i === 2) { rankBadge = "🥈 #2"; rankClass = "rank-cell-top2"; }
                else if (i === 3) { rankBadge = "🥉 #3"; rankClass = "rank-cell-top3"; }

                const leaderText = leader ? `${leader.alias}` : `<span style="color:#64748b;">(Vacante)</span>`;
                const crystalsReward = r ? `+${r.crystalsReward.toLocaleString()} 💎` : "—";
                const orbsReward = r ? `+${r.orbsReward.toLocaleString()} 🔮` : "—";

                const holoStyleClass = `hologram-${r?.hologramStyle || "cyber_gold"}`;
                const titlePreview = r
                    ? `<span class="hologram-pill ${holoStyleClass}" style="border-color:${r.primaryColor}; color:${r.secondaryColor};">
                        ${SeasonPrizesUI.formatTitleBadge(r.icon, r.titleName)}
                       </span>`
                    : "—";

                rowsHtml += `
                    <tr>
                        <td class="${rankClass}">${rankBadge}</td>
                        <td><strong>${leaderText}</strong></td>
                        <td style="color:#67e8f9; font-weight:700;">${crystalsReward}</td>
                        <td style="color:#c084fc; font-weight:700;">${orbsReward}</td>
                        <td>${titlePreview}</td>
                    </tr>
                `;
            }

            tableWrap.innerHTML = `
                <table class="season-rewards-table">
                    <thead>
                        <tr>
                            <th>Puesto</th>
                            <th>Líder en Vivo</th>
                            <th>Cristales</th>
                            <th>Orbes</th>
                            <th>Título Honorífico Holográfico</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rowsHtml}
                    </tbody>
                </table>
            `;
        };

        catTabs.querySelectorAll(".season-cat-btn").forEach((btn) => {
            btn.addEventListener("click", () => {
                catTabs.querySelectorAll(".season-cat-btn").forEach((b) => b.classList.remove("active"));
                btn.classList.add("active");
                this.activeCategory = btn.getAttribute("data-cat") as any;
                renderRewardsTable();
            });
        });

        prizesSection.appendChild(catTabs);
        prizesSection.appendChild(tableWrap);
        renderRewardsTable();
        mainWrap.appendChild(prizesSection);

        // 4. Armería de Títulos del Jugador
        const armorySection = document.createElement("div");
        armorySection.className = "season-armory-section";
        armorySection.id = "season-armory-section";

        this.renderArmorySection(armorySection, data, container);
        mainWrap.appendChild(armorySection);
        container.appendChild(mainWrap);
    }

    /**
     * Renderiza la armería del jugador con cápsula de título equipado, sub-pestañas de categoría y catálogo especial.
     */
    private static renderArmorySection(
        armorySection: HTMLElement,
        data: SeasonOverviewData,
        container: HTMLElement
    ): void {
        const myTitles = data.unlockedTitles || [];
        const equippedTitleId = data.equippedTitle || "";

        // Título actualmente equipado (cápsula destacada superior si existe)
        let equippedCapsuleHtml = "";
        if (equippedTitleId) {
            const eqTitle = myTitles.find((t) => (t.id || t.titleId) === equippedTitleId);
            const eqIcon = eqTitle?.icon || "👑";
            const eqName = eqTitle?.name || equippedTitleId;
            const eqStyle = eqTitle?.hologramStyle || "cyber_gold";
            const eqPrimary = eqTitle?.primaryColor || "#fbbf24";
            const eqSecondary = eqTitle?.secondaryColor || "#fef08a";
            const eqBadge = SeasonPrizesUI.formatTitleBadge(eqIcon, eqName);

            equippedCapsuleHtml = `
                <div class="season-equipped-banner" id="season-equipped-capsule">
                    <div class="season-equipped-left">
                        <span class="season-equipped-label">👑 TÍTULO EQUIPADO ACTUALMENTE</span>
                        <span class="hologram-pill hologram-${eqStyle}" style="border-color:${eqPrimary}; color:${eqSecondary};">
                            ${eqBadge}
                        </span>
                    </div>
                    <button type="button" class="season-equip-btn is-equipped" id="btn-unequip-active-title" data-equip-id="${equippedTitleId}">
                        Desequipar
                    </button>
                </div>
            `;
        }

        // Sub-pestañas de filtro por categoría
        const tabsHtml = `
            <div class="season-armory-tabs" id="armory-filter-tabs">
                <button type="button" class="armory-filter-btn ${this.armoryFilter === "all" ? "active" : ""}" data-filter="all">👑 Todos (${myTitles.length})</button>
                <button type="button" class="armory-filter-btn ${this.armoryFilter === "ranking_kills" ? "active" : ""}" data-filter="ranking_kills">⚔️ Bajas</button>
                <button type="button" class="armory-filter-btn ${this.armoryFilter === "ranking_mass" ? "active" : ""}" data-filter="ranking_mass">🪐 Masa</button>
                <button type="button" class="armory-filter-btn ${this.armoryFilter === "ranking_streak" ? "active" : ""}" data-filter="ranking_streak">🔥 Racha</button>
                <button type="button" class="armory-filter-btn ${this.armoryFilter === "ranking_time" ? "active" : ""}" data-filter="ranking_time">⏳ Tiempo</button>
                <button type="button" class="armory-filter-btn ${this.armoryFilter === "special" ? "active" : ""}" data-filter="special">⚡ Especiales</button>
            </div>
        `;

        // Catálogo de Títulos Especiales Adictivos
        const specialTitles = data.specialTitles || [];
        let specialCardsHtml = "";
        for (const st of specialTitles) {
            const holoStyleClass = `hologram-${st.hologramStyle || "cyber_gold"}`;
            const formattedTitle = SeasonPrizesUI.formatTitleBadge(st.icon, st.name);
            specialCardsHtml += `
                <div class="special-titles-card">
                    <div class="special-title-top">
                        <span class="hologram-pill ${holoStyleClass}" style="border-color:${st.primaryColor}; color:${st.secondaryColor};">
                            ${formattedTitle}
                        </span>
                        <span class="special-duration-badge">⏱️ Dura ${st.durationDays}d</span>
                    </div>
                    <div class="special-title-req">🎯 ${st.description}</div>
                </div>
            `;
        }

        armorySection.innerHTML = `
            <h3 class="season-armory-title">👑 Mis Títulos Honoríficos y Especiales</h3>
            ${equippedCapsuleHtml}
            ${tabsHtml}
            <div class="season-armory-grid" id="my-titles-grid">
                <!-- Se rellena reactivamente según el filtro -->
            </div>

            <h4 style="margin: 16px 0 4px 0; font-size: 0.9rem; color: #fbbf24; text-transform: uppercase; letter-spacing: 0.5px;">
                ⚡ Títulos Especiales Adictivos (Desbloqueables en Combate)
            </h4>
            <div class="season-armory-grid">
                ${specialCardsHtml}
            </div>
        `;

        // Función para renderizar únicamente las tarjetas del grid según el filtro
        const gridEl = armorySection.querySelector("#my-titles-grid") as HTMLElement;
        const renderGrid = () => {
            if (!gridEl) return;
            const filter = this.armoryFilter;
            const filteredTitles = filter === "all"
                ? myTitles
                : myTitles.filter((t) => t.category === filter);

            if (filteredTitles.length === 0) {
                gridEl.innerHTML = `
                    <div style="grid-column: 1/-1; padding: 16px; text-align: center; color: #94a3b8; font-size: 0.85rem; background: rgba(0,0,0,0.25); border-radius: 8px;">
                        ${myTitles.length === 0
                            ? "🛡️ Aún no posees títulos honoríficos. ¡Alcanza el Top 5 en cualquier ranking mensual o completa hazañas especiales en la arena para desbloquearlos!"
                            : "No posees títulos desbloqueados en esta categoría."}
                    </div>
                `;
                return;
            }

            let cardsHtml = "";
            for (const t of filteredTitles) {
                const titleId = t.id || t.titleId;
                const isEquipped = titleId === data.equippedTitle;
                const holoStyleClass = `hologram-${t.hologramStyle || "cyber_gold"}`;
                const expDate = t.expiresAt ? new Date(t.expiresAt) : null;
                const timeRemainingStr = expDate ? this.formatRemainingTime(expDate.getTime() - Date.now()) : "Permanente";
                const formattedTitle = SeasonPrizesUI.formatTitleBadge(t.icon, t.name);

                cardsHtml += `
                    <div class="season-title-card ${isEquipped ? "equipped" : ""}" data-title-id="${titleId}">
                        <div class="season-title-info">
                            <span class="hologram-pill ${holoStyleClass}" style="border-color:${t.primaryColor}; color:${t.secondaryColor};">
                                ${formattedTitle}
                            </span>
                            <span class="season-title-meta">⏳ Expira en: ${timeRemainingStr}</span>
                        </div>
                        <button type="button" class="season-equip-btn ${isEquipped ? "is-equipped" : ""}" data-equip-id="${titleId}">
                            ${isEquipped ? "✓ Equipado (Quitar)" : "Equipar"}
                        </button>
                    </div>
                `;
            }

            gridEl.innerHTML = cardsHtml;

            // Conectar botones de equipar dentro del grid
            gridEl.querySelectorAll<HTMLButtonElement>("[data-equip-id]").forEach((btn) => {
                btn.addEventListener("click", async () => {
                    const tid = btn.getAttribute("data-equip-id");
                    if (!tid) return;
                    const isCurrentlyEquipped = tid === data.equippedTitle;
                    const titleToEquip = isCurrentlyEquipped ? "" : tid;
                    await this.handleEquipTitle(titleToEquip, container);
                });
            });
        };

        // Conectar botón de desequipar en la cápsula superior (si existe)
        const unequipBtn = armorySection.querySelector("#btn-unequip-active-title");
        if (unequipBtn) {
            unequipBtn.addEventListener("click", async () => {
                await this.handleEquipTitle("", container);
            });
        }

        // Conectar pestañas de filtro reactivas
        const tabsWrap = armorySection.querySelector("#armory-filter-tabs");
        if (tabsWrap) {
            tabsWrap.querySelectorAll<HTMLButtonElement>(".armory-filter-btn").forEach((btn) => {
                btn.addEventListener("click", () => {
                    tabsWrap.querySelectorAll(".armory-filter-btn").forEach((b) => b.classList.remove("active"));
                    btn.classList.add("active");
                    this.armoryFilter = btn.getAttribute("data-filter") || "all";
                    renderGrid();
                });
            });
        }

        renderGrid();
    }

    /**
     * Procesa la solicitud para equipar o desequipar un título honorífico.
     */
    private static async handleEquipTitle(titleId: string, container: HTMLElement): Promise<void> {
        const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
        if (!token) {
            Swal.fire({
                title: "Inicia Sesión",
                text: "Debes iniciar sesión para equipar tus títulos honoríficos.",
                icon: "info",
                background: "#0f172a",
                color: "#f8fafc",
            });
            return;
        }

        try {
            const res = await fetch("/api/season/equip-title", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ titleId }),
            });

            const result = await res.json();
            if (result.success) {
                const newEquippedTitle = typeof result.equippedTitle === "string" ? result.equippedTitle : titleId;
                if (this.cachedOverview) {
                    this.cachedOverview.equippedTitle = newEquippedTitle;
                }

                window.dispatchEvent(new CustomEvent("alchem:title-equipped", { detail: { titleId: newEquippedTitle } }));

                if (titleId === "") {
                    Swal.fire({
                        title: "Título Desequipado",
                        text: "Has retirado tu título holográfico.",
                        icon: "success",
                        timer: 1500,
                        showConfirmButton: false,
                        background: "#0f172a",
                        color: "#f8fafc",
                    });
                } else {
                    Swal.fire({
                        title: "¡Título Equipado!",
                        text: "Tu holograma brillará sobre tu avatar en la arena.",
                        icon: "success",
                        timer: 1800,
                        showConfirmButton: false,
                        background: "#0f172a",
                        color: "#f8fafc",
                    });
                }

                // Actualizar reactivamente la sección de armería preservando el filtro activo
                if (this.cachedOverview) {
                    const armorySec = container.querySelector("#season-armory-section");
                    if (armorySec) {
                        this.renderArmorySection(armorySec as HTMLElement, this.cachedOverview, container);
                        return;
                    }
                }
                await this.render(container, this.onBalancesUpdatedCallback);
            } else {
                Swal.fire({
                    title: "No se pudo equipar",
                    text: result.error || "El título no está disponible o ya expiró.",
                    icon: "error",
                    background: "#0f172a",
                    color: "#f8fafc",
                });
            }
        } catch (err) {
            console.error("[SeasonPrizesUI] Error equipando título:", err);
        }
    }

    /**
     * Procesa el reclamo de recompensas pendientes de fin de temporada.
     */
    private static async handleClaimRewards(btn: HTMLButtonElement, container: HTMLElement): Promise<void> {
        const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
        if (!token) return;

        btn.disabled = true;
        btn.innerHTML = `<span>⏳ Reclamando...</span>`;

        try {
            const res = await fetch("/api/season/claim-rewards", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const result = await res.json();
            if (result.success) {
                if (this.onBalancesUpdatedCallback && result.currentCrystals !== undefined && result.currentOrbs !== undefined) {
                    this.onBalancesUpdatedCallback(result.currentCrystals, result.currentOrbs);
                }

                // Generar desglose de premios para SweetAlert2
                const rewards = result.claimedRewards || [];
                let rewardsListHtml = "";
                for (const r of rewards) {
                    rewardsListHtml += `
                        <div style="background:rgba(255,255,255,0.05); padding:8px 12px; border-radius:8px; margin-bottom:6px; display:flex; justify-content:space-between; align-items:center; font-size:0.9rem;">
                            <span>🏆 Top ${r.rank} (${r.leaderboardType.toUpperCase()})</span>
                            <span style="color:#fbbf24; font-weight:bold;">+${r.crystals} 💎 | +${r.orbs} 🔮</span>
                        </div>
                    `;
                }

                Swal.fire({
                    title: "🎉 ¡RECOMPENSAS RECLAMADAS!",
                    html: `
                        <div style="color:#cbd5e1; font-size:0.95rem; line-height:1.5;">
                            <p>¡Felicitaciones por tu desempeño en la temporada!</p>
                            <div style="margin:16px 0; text-align:left;">
                                ${rewardsListHtml}
                            </div>
                            <div style="font-size:1.15rem; font-weight:bold; color:#fef08a; margin-top:12px;">
                                Total: +${result.totalCrystals} Cristales 💎 | +${result.totalOrbs} Orbes 🔮
                            </div>
                        </div>
                    `,
                    icon: "success",
                    confirmButtonText: "¡A la Arena!",
                    confirmButtonColor: "#f59e0b",
                    background: "#0f172a",
                    color: "#f8fafc",
                });

                await this.render(container, this.onBalancesUpdatedCallback);
            } else {
                Swal.fire({
                    title: "Aviso",
                    text: result.message || "No hay recompensas pendientes.",
                    icon: "info",
                    background: "#0f172a",
                    color: "#f8fafc",
                });
                btn.disabled = false;
                btn.innerHTML = `<span>👑 RECLAMAR RECOMPENSAS</span>`;
            }
        } catch (err) {
            console.error("[SeasonPrizesUI] Error reclamando premios:", err);
            btn.disabled = false;
            btn.innerHTML = `<span>👑 RECLAMAR RECOMPENSAS</span>`;
        }
    }

    private static formatDuration(ms: number): string {
        const totalMinutes = Math.floor(Math.max(0, ms) / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (hours > 0) return `${hours}h ${minutes}m`;
        const seconds = Math.floor((Math.max(0, ms) % 60000) / 1000);
        return `${minutes}m ${seconds}s`;
    }

    private static formatRemainingTime(ms: number): string {
        if (ms <= 0) return "Expirado";
        const totalHours = Math.floor(ms / 3600000);
        const days = Math.floor(totalHours / 24);
        const hours = totalHours % 24;
        if (days > 0) return `${days}d ${hours}h`;
        const minutes = Math.floor((ms % 3600000) / 60000);
        return `${hours}h ${minutes}m`;
    }
}
